import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

import { customerService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import UndoSnackbar from '../../../components/dashboard/UndoSnackbar';
import useUndoDelete from '../../../components/dashboard/useUndoDelete';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { normalizeTaxId } from '../../../components/dashboard/companyFields';
import { formatTaxId } from '../../../utils/formatters';
import { BusinessStatusChip, businessStatus, CompanyTypeChip } from '../../../components/dashboard/BusinessChips';
import { buildBusinessActions, buildBusinessColumns, businessCapabilities } from './columns';

// Only internal users ever receive soft-deleted businesses from the API.
const STATUS_OPTIONS = ['active', 'inactive'];
const INTERNAL_STATUS_OPTIONS = [...STATUS_OPTIONS, 'deleted'];
const COMPANY_TYPE_OPTIONS = ['independent', 'headquarters', 'branch'];
const CUSTOMER_PARAM = 'customer';

const normalize = (value) => (value ?? '').toString().toLowerCase();

export default function BusinessesList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const { collaborator } = useAuth();
  // Creating/deleting stores depends on the customer's contract: internal
  // users only (the backend enforces it too). Edit comes per record.
  const isInternal = Boolean(collaborator?.is_internal);

  const [status, setStatus] = useState('');
  const [companyType, setCompanyType] = useState('');
  // Deep-linkable (the customer detail page links here with ?customer=).
  const customer = (isInternal && searchParams.get(CUSTOMER_PARAM)) || '';
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectionModel, setSelectionModel] = useState([]);
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);

  const businessesQuery = useQuery({
    queryKey: ['businesses'],
    retry: retryOnServerError,
    queryFn: () => customerService.listBusinesses().then((res) => res.data),
  });

  const businesses = useMemo(() => businessesQuery.data ?? [], [businessesQuery.data]);

  const customerOptions = useMemo(() => {
    const byUuid = new Map(businesses.map((business) => [business.customer_uuid, business.customer_name]));
    return [...byUuid.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [businesses]);

  const matchesFilters = useCallback((business, { statusValue, companyTypeValue, customerValue, searchValue }) => {
    const businessStatusValue = businessStatus(business);
    // No status filter = every live business; deleted ones only show up
    // when explicitly asked for (so they can be restored).
    if (statusValue === '' && businessStatusValue === 'deleted') return false;
    if (statusValue !== '' && businessStatusValue !== statusValue) return false;
    if (companyTypeValue !== '' && business.company_type !== companyTypeValue) return false;
    if (customerValue !== '' && business.customer_uuid !== customerValue) return false;
    if (searchValue) {
      const term = normalize(searchValue);
      const taxTerm = normalizeTaxId(searchValue);
      const matches =
        normalize(business.display_name).includes(term) ||
        normalize(business.legal_name).includes(term) ||
        normalize(business.customer_name).includes(term) ||
        (taxTerm !== '' && normalizeTaxId(business.tax_id).includes(taxTerm));
      if (!matches) return false;
    }
    return true;
  }, []);

  const currentFilters = { statusValue: status, companyTypeValue: companyType, customerValue: customer, searchValue: search };

  const filteredBusinesses = useMemo(
    () => businesses.filter((business) => matchesFilters(business, currentFilters)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [businesses, status, companyType, customer, search, matchesFilters],
  );

  const applySelection = (overrides) => {
    const filters = { ...currentFilters, ...overrides };
    const visibleIds = businesses
      .filter((business) => matchesFilters(business, filters))
      .map((business) => business.uuid);
    setSelectionModel((prev) => prev.filter((id) => visibleIds.includes(id)));
  };

  const updateStatus = (value) => {
    setStatus(value);
    applySelection({ statusValue: value });
  };

  const updateCompanyType = (value) => {
    setCompanyType(value);
    applySelection({ companyTypeValue: value });
  };

  const updateCustomer = (value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(CUSTOMER_PARAM, value);
        else next.delete(CUSTOMER_PARAM);
        return next;
      },
      { replace: true },
    );
    applySelection({ customerValue: value });
  };

  const updateSearch = (value) => {
    setSearch(value);
    applySelection({ searchValue: value });
  };

  const clearFilters = () => {
    setStatus('');
    setCompanyType('');
    setSearch('');
    updateCustomer('');
    applySelection({ statusValue: '', companyTypeValue: '', customerValue: '', searchValue: '' });
  };

  const invalidateBusinesses = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['businesses'] }),
      // Customer pages show the stores list/count.
      queryClient.invalidateQueries({ queryKey: ['customers'] }),
    ]);

  const { pending, remove, undo, dismiss, seed } = useUndoDelete({
    onDelete: (uuid) => customerService.deleteBusiness(uuid).then(invalidateBusinesses),
    onRestore: (uuid) => customerService.restoreBusiness(uuid).then(invalidateBusinesses),
  });

  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    const seedDelete = location.state?.seedDelete;
    if (seedDelete) {
      seed(seedDelete.ids, seedDelete.message);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
      return;
    }
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleDeleteOne = (business) => {
    remove(business.uuid, t('businesses.deleteSuccess'));
    setSelectionModel((prev) => prev.filter((id) => id !== business.uuid));
  };

  const handleDeleteSelected = () => {
    const ids = [...selectionModel];
    remove(ids, t('businesses.deleteSuccess'));
    setSelectionModel([]);
  };

  const handleRestore = async (business) => {
    await customerService.restoreBusiness(business.uuid);
    await invalidateBusinesses();
    setFlashMessage(t('businesses.restoreSuccess'));
  };

  const actionHandlers = {
    onView: (business) => navigate(`/businesses/${business.uuid}`),
    onEdit: (business) => navigate(`/businesses/${business.uuid}/edit`),
    onDelete: handleDeleteOne,
    onRestore: handleRestore,
  };

  const columns = useMemo(
    () => buildBusinessColumns({ t, language: i18n.language, isInternal, ...actionHandlers }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, navigate, isInternal],
  );

  const chips = [];
  if (status !== '') {
    chips.push({
      key: 'status',
      label: `${t('businesses.filters.status')}: ${t(`businesses.statuses.${status}`)}`,
      onDelete: () => updateStatus(''),
    });
  }
  if (companyType !== '') {
    chips.push({
      key: 'companyType',
      label: `${t('businesses.filters.companyType')}: ${t(`businesses.companyTypes.${companyType}`)}`,
      onDelete: () => updateCompanyType(''),
    });
  }
  if (customer !== '') {
    chips.push({
      key: 'customer',
      label: `${t('businesses.filters.customer')}: ${customerOptions.find((option) => option.value === customer)?.label ?? ''}`,
      onDelete: () => updateCustomer(''),
    });
  }

  const newBusinessPath = customer ? `/businesses/new?${CUSTOMER_PARAM}=${customer}` : '/businesses/new';

  const header = (
    <PageHeader
      title={t('businesses.listTitle')}
      breadcrumbs={[
        { label: t('businesses.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('businesses.breadcrumb.list') },
      ]}
      actions={
        isInternal && (
          <Button variant="contained" onClick={() => navigate(newBusinessPath)}>
            {t('businesses.newBusiness')}
          </Button>
        )
      }
    />
  );

  if (businessesQuery.isError) {
    const httpStatus = businessesQuery.error?.response?.status;
    return (
      <BoundedPageLayout header={header}>
        {httpStatus === 403 ? (
          <PermissionDenied
            title={t('businesses.states.permissionDeniedTitle')}
            message={t('businesses.states.permissionDeniedMessage')}
          />
        ) : (
          <ErrorState
            title={t('businesses.states.errorTitle')}
            message={t('businesses.states.errorMessage')}
            actionLabel={t('businesses.states.retry')}
            onRetry={businessesQuery.refetch}
          />
        )}
      </BoundedPageLayout>
    );
  }

  const selectedCount = selectionModel.length;
  const isSelectable = (business) => businessCapabilities(business).canDelete;

  const filters = [
    {
      field: 'status',
      label: t('businesses.filters.status'),
      value: status,
      onChange: updateStatus,
      options: (isInternal ? INTERNAL_STATUS_OPTIONS : STATUS_OPTIONS).map((value) => ({
        value,
        label: t(`businesses.statuses.${value}`),
      })),
    },
    {
      field: 'companyType',
      label: t('businesses.filters.companyType'),
      value: companyType,
      onChange: updateCompanyType,
      options: COMPANY_TYPE_OPTIONS.map((value) => ({ value, label: t(`businesses.companyTypes.${value}`) })),
    },
  ];
  if (isInternal) {
    filters.push({
      field: 'customer',
      label: t('businesses.filters.customer'),
      value: customer,
      onChange: updateCustomer,
      options: customerOptions,
    });
  }

  // Plain props object, NOT a function/component: `slots.toolbar` must stay a
  // stable reference (the imported ListFilterToolbar) across renders, or the
  // toolbar (and its focused search input) remounts on every keystroke.
  const toolbarProps = {
    filters,
    search: { label: t('businesses.filters.search'), value: search, onChange: updateSearch },
    chips,
    resultsLabel: t('businesses.filters.resultsFound', { count: filteredBusinesses.length }),
    clearLabel: t('businesses.filters.clear'),
    onClearAll: clearFilters,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
    bulkAction: isInternal && selectedCount > 0 && (
      <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteSelected}>
        {t('businesses.list.deleteSelected', { count: selectedCount })}
      </Button>
    ),
  };

  return (
    <BoundedPageLayout header={header}>
      <DataGridCard>
        {isMobile ? (
          <Box>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
            {filteredBusinesses.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              <List disablePadding>
                {filteredBusinesses
                  .slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize,
                  )
                  .map((business) => (
                    <ListItem
                      key={business.uuid}
                      divider
                      secondaryAction={
                        <RowActionsMenu actions={buildBusinessActions({ t, business, ...actionHandlers })} />
                      }
                    >
                      {isInternal && isSelectable(business) && (
                        <Checkbox
                          edge="start"
                          checked={selectionModel.includes(business.uuid)}
                          onChange={(event) =>
                            setSelectionModel((prev) =>
                              event.target.checked
                                ? [...prev, business.uuid]
                                : prev.filter((id) => id !== business.uuid),
                            )
                          }
                          sx={{ mr: 1 }}
                        />
                      )}
                      <ListItemText
                        primary={business.display_name}
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                            {isInternal && (
                              <Typography variant="caption" color="text.secondary">
                                {business.customer_name}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatTaxId(business.tax_id, business.country) || '—'}
                            </Typography>
                            <CompanyTypeChip t={t} companyType={business.company_type} />
                            <BusinessStatusChip t={t} business={business} />
                          </Stack>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
              </List>
            )}
          </Box>
        ) : (
          <DataGrid
            // Name and type columns render two lines; a fixed density-based
            // row height clips that second line in compact mode, so let each
            // row size to its own content.
            getRowHeight={() => 'auto'}
            rows={filteredBusinesses}
            columns={columns}
            getRowId={(row) => row.uuid}
            loading={businessesQuery.isLoading}
            checkboxSelection={isInternal}
            isRowSelectable={(params) => isSelectable(params.row)}
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => setSelectionModel(model)}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            showColumnVerticalBorder={showColumnBorder}
            slots={{
              toolbar: ListFilterToolbar,
              noRowsOverlay: EmptyState,
              columnSelectorIcon: ViewColumnOutlinedIcon,
            }}
            slotProps={{
              toolbar: toolbarProps,
              noRowsOverlay: { t },
            }}
            localeText={{
              toolbarColumns: t('businesses.filters.columns'),
              toolbarFilters: t('businesses.filters.filters'),
            }}
            sx={{
              border: 0,
              ...(showCellBorder && {
                '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
              }),
            }}
          />
        )}
      </DataGridCard>

      <UndoSnackbar pending={pending} onUndo={undo} onDismiss={dismiss} undoLabel={t('businesses.deleteUndo')} />

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('businesses.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('businesses.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
